import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchPositionRecruitmentPlanEditor, RECRUITMENT_PLAN_EDITOR } from './planApi';
import {
    buildPlanPatchRequestBody,
    planSecondsToDisplayDays,
    transformPlanFromApi,
} from './helper';
import getResponseJson from '@helpers/getResponseJson';
import { DEFAULT_METADATA, mapRecruitmentFlowStepsMeta } from '@components/RecruitmentFlowEditor/helper';
import { getFlowById, getRecruitmentFlowStepsMeta } from '@components/RecruitmentFlowEditor/recruitmentFlowApi';
import { useTranslations } from '@components/context';
import { callLinkAction } from '@helpers/fetchCustom';
import { notification } from '@utils/utils';

export function usePositionFlowPlan(jobId) {
    const { t } = useTranslations();

    const [data, setData] = useState(null);
    const [flow, setFlow] = useState(null);
    const [plan, setPlan] = useState({});
    const [metadata, setMetadata] = useState({ ...DEFAULT_METADATA });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [mainLoading, setMainLoading] = useState(true);

    const loadingRef = useRef(false);
    const pendingFlowIdRef = useRef(null);

    const links = data?._links || {};

    useEffect(() => {
        (async () => {
            setMainLoading(true);
            try {
                const [metadataResponse, editorResponse] = await Promise.all([
                    getRecruitmentFlowStepsMeta(),
                    fetchPositionRecruitmentPlanEditor(jobId),
                ]);
                const metadataData = await getResponseJson(metadataResponse);
                const editorData = await getResponseJson(editorResponse);

                setMetadata(mapRecruitmentFlowStepsMeta(metadataData, t));
                setData(editorData[RECRUITMENT_PLAN_EDITOR]);
            } catch (e) {
                console.error(e);
                throw e;
            } finally {
                setMainLoading(false);
            }
        })();
    }, [jobId]);

    useEffect(() => {
        if (data && !mainLoading) {
            fetchJobPlan(jobId);
        }
    }, [data, mainLoading, jobId]);

    const fetchJobPlan = useCallback(
        async () => {
            if(!links?.get) return;
            loadingRef.current = true;
            setLoading(true);
            try {
                const response = await callLinkAction(links, 'get');
                if (!response.ok) return;
                const res = await getResponseJson(response);
                setPlan(transformPlanFromApi(res.plan || {}));
                setFlow(res.flow);
            } catch (e) {
                console.error(e);
                throw e;
            } finally {
                loadingRef.current = false;
                setLoading(false);
            }
        },
        [links],
    );  

    const fetchFlowById = useCallback(async (flowId) => {
        if (!flowId || Number(flowId) === Number(flow?.id)) {
            pendingFlowIdRef.current = null;
            return;
        }
        if (loadingRef.current) {
            pendingFlowIdRef.current = flowId;
            return;
        }
        try {
            loadingRef.current = true;
            setLoading(true);
            const response = await getFlowById(flowId);
            if (!response.ok) return;
            const flowData = await getResponseJson(response);
            setFlow(flowData);
        } catch (err) {
            console.error('Error fetching flow steps:', err);
            if (err.responseJSON?.notification) {
                notification(err.responseJSON.notification);
            }
        } finally {
            loadingRef.current = false;
            setLoading(false);
            const pendingFlowId = pendingFlowIdRef.current;
            pendingFlowIdRef.current = null;
            if (pendingFlowId != null && Number(pendingFlowId) !== Number(flowId)) {
                fetchFlowById(pendingFlowId);
            }
        }
    }, [flow?.id]);

    const savePlan = useCallback(async (formData) => {
        if (!links?.patch) return false;
        try {
            const body = buildPlanPatchRequestBody(formData);
            const response = await callLinkAction(links, 'patch', { body: JSON.stringify(body) });
            const responseData = await getResponseJson(response);
            if (!response.ok) return false;
            setPlan(planSecondsToDisplayDays(responseData.plan));
            setFlow(responseData.flow)
            return responseData;
        } catch (err) {
            console.error('Error saving recruitment plan:', err);
            return false;
        }
    }, [links]);

    const mappedFlow = useMemo(() => {
        const updatedSteps = flow?.steps?.map((step) => {
            const planData = plan?.[step?.id] || {};
            return {
                ...step,
                deadline: planData?.deadline,
                target_lead_time: planData?.target_lead_time || step?.target_lead_time,
            };
        });

        return {
            ...flow,
            steps: updatedSteps,
        };
    }, [flow, plan]);

    const savedStepIds = useMemo(
        () => new Set((flow?.steps ?? []).map((step) => String(step.id))),
        [flow],
    );

    

    return {
        plan,
        flow: mappedFlow,
        savedStepIds,
        setPlan,
        loading,
        mainLoading,
        error,
        setError,
        fetchJobPlan,
        fetchFlowById,
        savePlan,
        metadata,
    };
}
